export interface MarkerPose {
  /** 0 to 1: a past self fades where its rail meets the present one. */
  fade?: number;
  x: number;
  y: number;
  partDx?: number;
  scale: number;
  opacity: number;
  visible: boolean;
  behind: boolean;
  nameOpacity: number;
  /** Location stack sits above the rail (negative lane). */
  stackAbove?: boolean;
  /** Paint order for location stacks on the same beat. */
  layerZ?: number;
}

export interface MarkerKeyframe {
  ms: number;
  pose: MarkerPose;
}

export interface MarkerTrack {
  keyframes: MarkerKeyframe[];
}

export const HIDDEN_POSE: MarkerPose = {
  x: 0,
  y: 0,
  scale: 1,
  opacity: 0,
  visible: false,
  behind: false,
  nameOpacity: 0,
};

export function pushKeyframe(
  track: MarkerTrack,
  ms: number,
  pose: MarkerPose,
): void {
  const last = track.keyframes.at(-1);
  if (last !== undefined && last.ms === ms) {
    last.pose = pose;
    return;
  }
  const before = track.keyframes.at(-2);
  // Holding still needs only the two ends. A book has thousands of moments.
  if (last !== undefined && before !== undefined
    && samePose(before.pose, last.pose) && samePose(last.pose, pose)) {
    last.ms = ms;
    return;
  }
  track.keyframes.push({ ms, pose });
}

function samePose(a: MarkerPose, b: MarkerPose): boolean {
  return a.x === b.x && a.y === b.y && a.partDx === b.partDx && a.scale === b.scale
    && a.opacity === b.opacity && a.visible === b.visible && a.behind === b.behind
    && a.nameOpacity === b.nameOpacity && a.stackAbove === b.stackAbove
    && a.layerZ === b.layerZ;
}

/** The last keyframe at or before this time, found without walking the track every frame. */
function frameIndexAt(frames: readonly MarkerKeyframe[], ms: number): number {
  let low = 0;
  let high = frames.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (frames[middle]!.ms <= ms) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return low;
}

export function sampleMarkerTrack(
  track: MarkerTrack,
  playheadMs: number,
): MarkerPose {
  const frames = track.keyframes;
  if (frames.length === 0) {
    return HIDDEN_POSE;
  }
  if (playheadMs <= frames[0].ms) {
    return frames[0].pose;
  }
  const last = frames.at(-1);
  if (last !== undefined && playheadMs >= last.ms) {
    return last.pose;
  }
  const index = frameIndexAt(frames, playheadMs);
  const prev = frames[index]!;
  const next = frames[index + 1]!;
  if (playheadMs === prev.ms) {
    return prev.pose;
  }
  return interpolatePose(prev.pose, next.pose, (playheadMs - prev.ms) / (next.ms - prev.ms));
}

function interpolatePose(a: MarkerPose, b: MarkerPose, t: number): MarkerPose {
  const mix = (x: number, y: number): number => x + (y - x) * t;
  if (!a.visible || !b.visible) {
    const anchor = a.visible ? a : b;
    return {
      x: anchor.x,
      y: anchor.y,
      partDx: anchor.partDx,
      scale: mix(a.scale, b.scale),
      opacity: mix(a.opacity, b.opacity),
      visible: t < 0.5 ? a.visible : b.visible,
      behind: t < 0.5 ? a.behind : b.behind,
      nameOpacity: mix(a.nameOpacity, b.nameOpacity),
      stackAbove: t < 0.5 ? a.stackAbove : b.stackAbove,
      layerZ: t < 0.5 ? a.layerZ : b.layerZ,
    };
  }
  return {
    x: mix(a.x, b.x),
    y: mix(a.y, b.y),
    partDx: a.partDx === undefined && b.partDx === undefined
      ? undefined
      : mix(a.partDx ?? 0, b.partDx ?? 0),
    scale: mix(a.scale, b.scale),
    opacity: mix(a.opacity, b.opacity),
    visible: true,
    behind: t < 0.5 ? a.behind : b.behind,
    nameOpacity: mix(a.nameOpacity, b.nameOpacity),
    stackAbove: t < 0.5 ? a.stackAbove : b.stackAbove,
    layerZ: t < 0.5 ? a.layerZ : b.layerZ,
  };
}
